<template>
  <v-dialog :value="value" max-width="620" persistent @input="close">
    <v-card class="freq" v-if="plan">
      <v-card-title class="freq__title">{{
        $t("frequencyChange.title")
      }}</v-card-title>

      <v-card-text>
        <!-- O aviso que o card pede: mudar a frequência não é só trocar um
             número, é redefinir a grade. -->
        <p class="freq__lead">
          <template v-if="plan.action === 'ADD_SLOTS'">
            {{
              $t("frequencyChange.addSlots", {
                required: plan.requiredSlots,
                count: plan.slotsToAdd,
                slots:
                  plan.slotsToAdd === 1
                    ? $t("frequencyChange.newSlot")
                    : $t("frequencyChange.newSlots"),
              })
            }}
          </template>
          <template v-else-if="plan.action === 'PICK_SLOTS'">
            {{
              $t("frequencyChange.pickSlots", {
                required: plan.requiredSlots,
                slots:
                  plan.requiredSlots === 1
                    ? $t("frequencyChange.slot")
                    : $t("frequencyChange.slots"),
                which:
                  plan.requiredSlots === 1
                    ? $t("frequencyChange.whichOne")
                    : $t("frequencyChange.whichMany"),
              })
            }}
          </template>
          <template v-else>
            {{ $t("frequencyChange.biweekly") }}
          </template>
        </p>

        <div class="freq__section">
          <div class="freq__section-title">
            {{ $t("frequencyChange.schedule") }}
          </div>

          <label
            v-for="slot in plan.currentSchedules"
            :key="slotKey(slot)"
            class="freq__slot"
            :class="{ 'freq__slot--on': isSelected(slot) }"
          >
            <input
              type="checkbox"
              :checked="isSelected(slot)"
              :disabled="plan.action === 'KEEP_SLOTS'"
              @change="toggle(slot)"
            />
            <span>{{ slotLabel(slot) }}</span>
          </label>
        </div>

        <div
          v-if="plan.action !== 'KEEP_SLOTS' && plan.availableWindows.length"
          class="freq__section"
        >
          <div class="freq__section-title">
            {{ $t("frequencyChange.addSlot") }}
          </div>
          <p class="freq__hint">
            {{ $t("frequencyChange.addHint") }}
          </p>

          <div class="freq__new">
            <v-select
              v-model="newWindow"
              :items="windowOptions"
              item-text="text"
              item-value="value"
              :label="$t('common.day')"
              outlined
              dense
              hide-details
            />
            <v-text-field
              v-model="newStart"
              type="time"
              :label="$t('common.start')"
              outlined
              dense
              hide-details
            />
            <v-btn
              text
              small
              color="#E8276F"
              :disabled="!canAddSlot"
              @click="addSlot"
            >
              {{ $t("common.add") }}
            </v-btn>
          </div>

          <div v-if="addedSlots.length" class="freq__added">
            <span
              v-for="slot in addedSlots"
              :key="slotKey(slot)"
              class="freq__chip"
            >
              {{ slotLabel(slot) }}
              <button type="button" @click="removeAdded(slot)">×</button>
            </span>
          </div>
        </div>

        <p class="freq__count" :class="{ 'freq__count--ok': isComplete }">
          {{
            $tc("frequencyChange.count", plan.requiredSlots, {
              selected: selected.length,
              required: plan.requiredSlots,
            })
          }}
        </p>

        <!-- O professor precisa saber o alcance antes de confirmar: as aulas
             futuras são remarcadas, as já dadas ficam como estão. -->
        <p class="freq__warn">
          {{ $t("frequencyChange.warn") }}
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" @click="close">{{
          $t("common.cancel")
        }}</v-btn>
        <v-btn
          color="#E8276F"
          dark
          depressed
          :loading="busy"
          :disabled="!isComplete || busy"
          @click="$emit('confirm', selected)"
        >
          {{ $t("frequencyChange.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { WEEK_DAY_ITEMS } from "@/shared/classPresentation";

// Card 86ak6kqa1. O diálogo é o "aviso" que a descrição pede: mudar a
// frequência exige redefinir a grade, e as três situações (aumentar, reduzir,
// quinzenal) diferem só em quantos horários a nova frequência comporta.
export default {
  name: "FrequencyChangeDialog",

  props: {
    value: { type: Boolean, default: false },
    plan: { type: Object, default: null },
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      selected: [],
      addedSlots: [],
      newWindow: null,
      newStart: null,
    };
  },

  computed: {
    windowOptions() {
      return (this.plan?.availableWindows || []).map((window) => ({
        value: window,
        text: this.$t("common.slotDayTime", {
          day: this.weekDayLabel(window.weekDay),
          from: window.from,
          to: window.to,
        }),
      }));
    },
    isComplete() {
      return this.selected.length === this.plan?.requiredSlots;
    },
    // A duração da aula vem da grade atual; sem grade, uma hora.
    lessonMinutes() {
      const first = this.plan?.currentSchedules?.[0];

      if (!first) return 60;

      return Math.max(
        this.toMinutes(first.endHour) - this.toMinutes(first.startHour),
        30
      );
    },
    canAddSlot() {
      if (!this.newWindow || !this.newStart) return false;

      const end = this.addMinutes(this.newStart, this.lessonMinutes);

      // Só dentro da janela informada — a API recusaria de qualquer forma, mas
      // deixar o botão ativo para algo que vai falhar é convidar ao erro.
      return this.newStart >= this.newWindow.from && end <= this.newWindow.to;
    },
  },

  watch: {
    plan: {
      immediate: true,
      handler(plan) {
        this.addedSlots = [];
        this.newWindow = null;
        this.newStart = null;

        if (!plan) {
          this.selected = [];
          return;
        }

        // Aumentar e manter partem da grade atual; reduzir começa vazio, porque
        // a escolha é justamente qual manter.
        this.selected =
          plan.action === "PICK_SLOTS" ? [] : [...plan.currentSchedules];
      },
    },
  },

  methods: {
    weekDayLabel(weekDay) {
      const item = WEEK_DAY_ITEMS.find((day) => day.value === Number(weekDay));
      return item ? item.text : this.$t("common.day");
    },
    slotKey(slot) {
      return `${slot.weekDay}|${slot.startHour}`;
    },
    slotLabel(slot) {
      return this.$t("common.slotDayTime", {
        day: this.weekDayLabel(slot.weekDay),
        from: slot.startHour,
        to: slot.endHour,
      });
    },
    isSelected(slot) {
      return this.selected.some(
        (item) => this.slotKey(item) === this.slotKey(slot)
      );
    },
    toggle(slot) {
      if (this.isSelected(slot)) {
        this.selected = this.selected.filter(
          (item) => this.slotKey(item) !== this.slotKey(slot)
        );
        return;
      }

      // Passar do necessário não é erro do professor, é falta de aviso: o botão
      // de confirmar só libera na conta exata.
      this.selected = [...this.selected, slot];
    },
    toMinutes(hour) {
      const [h, m] = String(hour || "0:0")
        .split(":")
        .map(Number);
      return h * 60 + m;
    },
    addMinutes(hour, minutes) {
      const total = this.toMinutes(hour) + minutes;
      const h = String(Math.floor(total / 60)).padStart(2, "0");
      const m = String(total % 60).padStart(2, "0");
      return `${h}:${m}`;
    },
    addSlot() {
      if (!this.canAddSlot) return;

      const slot = {
        weekDay: this.newWindow.weekDay,
        startHour: this.newStart,
        endHour: this.addMinutes(this.newStart, this.lessonMinutes),
      };

      if (this.isSelected(slot)) return;

      this.addedSlots = [...this.addedSlots, slot];
      this.selected = [...this.selected, slot];
      this.newStart = null;
    },
    removeAdded(slot) {
      this.addedSlots = this.addedSlots.filter(
        (item) => this.slotKey(item) !== this.slotKey(slot)
      );
      this.selected = this.selected.filter(
        (item) => this.slotKey(item) !== this.slotKey(slot)
      );
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.freq__title {
  font-size: 1.1rem;
  font-weight: 700;
}

.freq__lead {
  font-size: 0.95rem;
  color: var(--dk-ink-soft, #4b4551);
}

.freq__section {
  margin-top: 18px;
}

.freq__section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dk-muted, #6b6472);
  margin-bottom: 8px;
}

.freq__hint {
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
  margin-bottom: 8px;
}

.freq__slot {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e4e0e9;
  border-radius: 10px;
  margin-bottom: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}

.freq__slot--on {
  border-color: var(--dk-pink, #e8276f);
  background: rgba(232, 39, 111, 0.06);
}

.freq__new {
  display: grid;
  grid-template-columns: 1fr 130px auto;
  gap: 10px;
  align-items: center;
}

.freq__added {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.freq__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--dk-pink, #e8276f);
  border-radius: 999px;
  color: var(--dk-pink, #e8276f);
  font-size: 0.78rem;
  padding: 4px 10px;
}

.freq__chip button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
}

.freq__count {
  margin-top: 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--dk-ember-deep, #b4560f);
}

.freq__count--ok {
  color: var(--dk-teal, #00a379);
}

.freq__warn {
  margin-top: 10px;
  margin-bottom: 0;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
}

@media (max-width: 600px) {
  .freq__new {
    grid-template-columns: 1fr;
  }
}
</style>
